import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  Refresh,
  Info,
  FilterList
} from '@mui/icons-material';
import { brandColors } from '../../../theme/theme';
import { 
  getFilmTrackingOverview, 
  getFilmTrackingStats,
  getDistributors,
  getProductionCompanies
} from '../services/filmTrackingService';
import { getDefaultStartDate, getDefaultEndDate, getQuickFilterRanges, formatDateForDisplay } from '../../../lib/utils/dateUtils';
import { useFilmSubscription } from '../../../hooks/useFilmSubscription';
import BlurredContent from '../../../components/Subscription/BlurredContent';
import { FILM_TIERS } from '../../../lib/filmSubscriptionTiers';
import FilmTile from '../../../components/FilmTracking/FilmTile';
import FilmDashboardBanner from '../../../components/FilmTracking/FilmDashboardBanner';
import FilmAdvancedMetricsPills from '../components/DeepDive/FilmAdvancedMetricsPills';
import apiService from '../../../lib/services/apiService';

const FilmDeepDiveList = () => {
  const navigate = useNavigate();
  const { canAccessDeepDive } = useFilmSubscription();
  const [films, setFilms] = useState([]);
  const [stats, setStats] = useState({});
  const [metadata, setMetadata] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Deep dive data for each film
  const [filmDeepDiveData, setFilmDeepDiveData] = useState({});
  const [loadingDeepDive, setLoadingDeepDive] = useState({});
  
  // Enhanced filter states
  const [filters, setFilters] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    sortBy: 'release_date',
    sortOrder: 'asc',
    searchTerm: '',
    distributors: [],
    productionCompanies: []
  });
  
  // Pending filters (not yet applied)
  const [pendingFilters, setPendingFilters] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    sortBy: 'release_date',
    sortOrder: 'asc',
    searchTerm: '',
    distributors: [],
    productionCompanies: []
  });
  
  // Company options for filters
  const [distributorOptions, setDistributorOptions] = useState([]);
  const [productionCompanyOptions, setProductionCompanyOptions] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  // Quick filter ranges
  const quickRanges = getQuickFilterRanges();

  // Fetch film list
  const fetchData = async (customFilters = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const currentFilters = customFilters || filters;
      
      const [overviewResponse, statsData] = await Promise.all([
        getFilmTrackingOverview({
          startDate: currentFilters.startDate,
          endDate: currentFilters.endDate,
          sortBy: currentFilters.sortBy,
          sortOrder: currentFilters.sortOrder
        }),
        getFilmTrackingStats()
      ]);
      
      const filmsData = overviewResponse.films || [];
      setFilms(filmsData);
      setStats(statsData);
      setMetadata(overviewResponse.metadata || {});
      
      // Fetch deep dive data for all films
      fetchDeepDiveDataForFilms(filmsData);
    } catch (err) {
      console.error('Error fetching film tracking data:', err);
      setError('Failed to load film tracking data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch deep dive data for a single film
  const fetchDeepDiveDataForFilm = async (imdbId) => {
    if (!imdbId || !canAccessDeepDive) {
      return null;
    }

    try {
      setLoadingDeepDive(prev => ({ ...prev, [imdbId]: true }));
      
      const apiFilters = {
        aggregate: true,
        group_by: 'ethnicity,agegroup,gender,movie_frequency_category',
        imdb_id: imdbId,
        film_recency_order: 1  // Only most recent round
      };
      
      const response = await apiService.getFilmSurveyToplinesAggregated(apiFilters);
      
      if (response?.success && response.data?.length > 0) {
        setFilmDeepDiveData(prev => ({
          ...prev,
          [imdbId]: response.data
        }));
        return response.data;
      }
      
      return null;
    } catch (err) {
      console.error(`Error fetching deep dive data for ${imdbId}:`, err);
      return null;
    } finally {
      setLoadingDeepDive(prev => ({ ...prev, [imdbId]: false }));
    }
  };

  // Fetch deep dive data for all films (in batches to avoid overwhelming the API)
  const fetchDeepDiveDataForFilms = async (filmsList) => {
    if (!canAccessDeepDive) {
      return;
    }

    // Process films in smaller batches to avoid rate limiting
    const batchSize = 3; // Reduced from 5 to reduce parallel requests
    for (let i = 0; i < filmsList.length; i += batchSize) {
      const batch = filmsList.slice(i, i + batchSize);
      
      // Use allSettled so one failure doesn't break the batch
      const results = await Promise.allSettled(
        batch.map(film => {
          const imdbId = film.tt_id || film.imdb_id;
          return fetchDeepDiveDataForFilm(imdbId);
        })
      );
      
      // Log any failures but continue processing
      results.forEach((result, idx) => {
        if (result.status === 'rejected') {
          const imdbId = batch[idx]?.tt_id || batch[idx]?.imdb_id;
          console.warn(`⚠️ Failed to fetch deep dive data for ${imdbId}:`, result.reason);
        }
      });
      
      // Longer delay between batches to avoid rate limiting
      if (i + batchSize < filmsList.length) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Increased from 200ms to 1 second
      }
    }
  };

  // Apply client-side filters (search only)
  const getFilteredFilms = () => {
    let filtered = films;
    
    // Apply search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(film => 
        (film.film_title || '').toLowerCase().includes(searchLower) ||
        (film.imdb_id || '').toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  };
  
  const filteredFilms = getFilteredFilms();
  
  // Filter handlers
  const handleFilterChange = (key, value) => {
    setPendingFilters({ ...pendingFilters, [key]: value });
  };
  
  const handleQuickFilter = (rangeKey) => {
    const range = quickRanges[rangeKey];
    if (range) {
      setPendingFilters({
        ...pendingFilters,
        startDate: range.startDate,
        endDate: range.endDate
      });
    }
  };
  
  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    fetchData(pendingFilters);
  };
  
  const handleResetFilters = () => {
    const defaultFilters = {
      startDate: getDefaultStartDate(),
      endDate: getDefaultEndDate(),
      sortBy: 'release_date',
      sortOrder: 'asc',
      searchTerm: '',
      distributors: [],
      productionCompanies: []
    };
    setPendingFilters(defaultFilters);
    setFilters(defaultFilters);
    fetchData(defaultFilters);
  };

  // Clean film title by removing descriptions
  const cleanFilmTitle = (title) => {
    if (!title) return 'Unknown Film';
    
    const patterns = [
      / – .*$/,
      / - .*$/,
      /: .*$/,
      /\(.*\)$/,
      /\s+$/,
    ];
    
    let cleanTitle = title;
    patterns.forEach(pattern => {
      cleanTitle = cleanTitle.replace(pattern, '');
    });
    
    return cleanTitle.trim();
  };

  useEffect(() => {
    fetchData();
    fetchCompanyOptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch company options for filters
  const fetchCompanyOptions = async () => {
    try {
      setLoadingCompanies(true);
      const [distributors, productionCos] = await Promise.all([
        getDistributors(),
        getProductionCompanies()
      ]);
      setDistributorOptions(distributors);
      setProductionCompanyOptions(productionCos);
    } catch (error) {
      console.error('Failed to load company options:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleFilmClick = (ttId) => {
    navigate(`/film-tracking/deep-dive/${ttId}`);
  };

  const clampPercentage = (value = 0) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return 0;
    }
    return Math.min(Math.max(value, 0), 100);
  };

  const FilmCard = ({ film }) => {
    const imdbId = film.tt_id || film.imdb_id;
    const deepDiveData = filmDeepDiveData[imdbId];
    const isLoadingDeepDive = loadingDeepDive[imdbId];

    const footerLines = [
      film.total_responses ? `${film.total_responses.toLocaleString()} survey responses` : 'Survey responses unavailable',
      film.survey_period
        ? `Survey completed ${film.survey_period}`
        : film.survey_date
        ? `Survey completed ${formatDateForDisplay(film.survey_date)}`
        : null
    ].filter(Boolean);

    const renderMetricsSection = ({ metrics: tileMetrics, clampPercentage }) => {
      if (isLoadingDeepDive) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        );
      }

      if (!deepDiveData || deepDiveData.length === 0) {
        return (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Advanced metrics data not available
            </Typography>
          </Box>
        );
      }

      return (
        <FilmAdvancedMetricsPills 
          data={deepDiveData} 
          clampPercentage={clampPercentage}
        />
      );
    };

    const tileFilm = {
      ...film,
      imdb_title: cleanFilmTitle(film.imdb_title || film.film_title),
      release_date_display: film.release_date ? formatDateForDisplay(film.release_date) : null
    };

    return (
      <FilmTile
        film={tileFilm}
        metrics={[]}
        footerLines={footerLines}
        onClick={() => handleFilmClick(film.tt_id || film.imdb_id)}
        renderMetrics={renderMetricsSection}
        showSurveyDateInHeader={false}
        allowNavigation={canAccessDeepDive}
      />
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <IconButton onClick={fetchData} color="primary" size="large">
            <Refresh />
          </IconButton>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Click to retry
          </Typography>
        </Box>
      </Container>
    );
  }

  const content = (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
              Film Deep Dive
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Advanced metrics for tracking efficiency and opportunity
            </Typography>
          </Box>
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchData} color="primary" size="large">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        <FilmDashboardBanner stats={stats} />
      </Box>

      {/* Enhanced Filter Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Filter Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList sx={{ color: brandColors.primary }} />
            <Typography variant="h6" sx={{ color: brandColors.primary }}>
              Filter & Sort Films
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={handleResetFilters}
            startIcon={<Refresh />}
          >
            Reset Filters
          </Button>
        </Box>

        {/* Date Range and Sort Controls */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Date Range */}
          <Grid item xs={12} md={3}>
            <TextField
              label="Start Date"
              type="date"
              value={pendingFilters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="End Date"
              type="date"
              value={pendingFilters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>

          {/* Sort Controls */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={pendingFilters.sortBy}
                label="Sort By"
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                <MenuItem value="release_date">Release Date</MenuItem>
                <MenuItem value="awareness">Awareness</MenuItem>
                <MenuItem value="interest">Interest</MenuItem>
                <MenuItem value="heat">Heat Score</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Order</InputLabel>
              <Select
                value={pendingFilters.sortOrder}
                label="Order"
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Quick Filter Buttons */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Quick Date Ranges:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.entries(quickRanges).map(([key, range]) => (
              <Chip
                key={key}
                label={range.label}
                onClick={() => handleQuickFilter(key)}
                variant="outlined"
                size="small"
                sx={{ 
                  '&:hover': { 
                    backgroundColor: brandColors.primary + '20' 
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Search Control */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Search Films"
              value={pendingFilters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              placeholder="Search by title or IMDB ID..."
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>

        {/* Company Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={distributorOptions}
              value={pendingFilters.distributors}
              onChange={(event, newValue) => handleFilterChange('distributors', newValue)}
              loading={loadingCompanies}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Distributors"
                  placeholder="Select distributors..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option}
                      size="small"
                      {...chipProps}
                    />
                  );
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={productionCompanyOptions}
              value={pendingFilters.productionCompanies}
              onChange={(event, newValue) => handleFilterChange('productionCompanies', newValue)}
              loading={loadingCompanies}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Production Companies"
                  placeholder="Select production companies..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option}
                      size="small"
                      {...chipProps}
                    />
                  );
                })
              }
            />
          </Grid>
        </Grid>

        {/* Apply Filters Button */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            onClick={handleApplyFilters}
            startIcon={<FilterList />}
            sx={{
              backgroundColor: brandColors.secondary,
              '&:hover': {
                backgroundColor: brandColors.primary
              }
            }}
          >
            Apply Filters
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Adjust filters above, then click to apply
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          Showing {filteredFilms.length} of {films.length} films
          {metadata.release_date_range && (
            <span> • Release dates: {formatDateForDisplay(metadata.release_date_range.from)} to {formatDateForDisplay(metadata.release_date_range.to)}</span>
          )}
        </Typography>
      </Paper>

      {/* Films Grid */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Advanced Metrics Overview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Click on any film to view detailed analytics and trends
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {filteredFilms.map((film) => (
          <Grid item xs={12} sm={6} lg={4} key={film.tt_id}>
            <FilmCard film={film} />
          </Grid>
        ))}
      </Grid>

      {/* Legend */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <Info sx={{ mr: 1 }} />
          Advanced Metric Definitions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Excess Awareness
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aware but low interest - indicates potential inefficiency in marketing spend
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Outstanding Interest
            </Typography>
            <Typography variant="body2" color="text.secondary">
              High interest among not yet aware - reachable audience opportunity
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Aware Opening Weekend
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aware audience planning to attend opening weekend
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Outstanding Opening Weekend
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Not yet aware but planning opening weekend - expansion opportunity
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Theater Among Aware
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aware audiences with theater intent - theatrical appeal indicator
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Outstanding Theater
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Not yet aware with theater intent - addressable theater audience
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Intent Conversion
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aware with viewing intent - activation rate from awareness
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );

  // Only show blurred content if user doesn't have access (super_admins have access via forceUnlock)
  if (!canAccessDeepDive) {
    return (
      <BlurredContent
        requiredTier={FILM_TIERS.ENTERPRISE}
        featureName="Film Deep Dive"
        blurAmount={12}
      >
        {content}
      </BlurredContent>
    );
  }

  return content;
};

export default FilmDeepDiveList;
